import React, { useEffect, useState } from 'react';
import { ScrollView } from 'react-native';
import { Auth as Authorization } from 'aws-amplify';
import { useSelector, useDispatch } from 'react-redux';
import { IStore } from 'src/redux/reducers';
import * as LoginActions from '../../redux/actions/loginAction';
import { LOGIN_FAILED_MESSAGE } from "../../strings"
import { Button, FormControl, Icon, IconButton, Input, VStack, Text, Box } from 'native-base';
import { containerStyle } from 'src/styles/containerStyle';
import { inputStyle } from 'src/styles/inputStyle';
import ProgressBar from 'src/components/ProgressBar';
import InweonLogo from 'src/components/InweonLogo';
import * as AppActions from '../../redux/actions/appAction';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { formProps } from 'src/styles/formProps';
import { isElectron } from 'src/utils/miscUtils';
import { getText } from 'src/utils/i18n';
import { EXPLICIT_LOGOUT_STORAGE_KEY, LAST_GRAIN_PROFILE_STORAGE_KEY } from 'src/constants';
interface LoginFormState {
    username: string,
    password: string
}

interface LoginFormErrors {
    username?: string;
    password?: string;
    formError?: string;
}

export default function LoginPage({ navigation }: any) {

    const globalLogoutResponse = useSelector((store: IStore) => store.login.globalLogoutState.response);
    const loginTriggerRef = React.useRef<{
        started: boolean;
        fallbackTimer?: ReturnType<typeof setTimeout>;
    }>({ started: false, fallbackTimer: undefined });

    useEffect(() => {
        // On web we dispatch a best-effort "global logout" before sign-in to clear stale server-side sessions.
        // If that call fails (or is slow), it should not block login.
        if (globalLogoutResponse === "SUCCESS" || globalLogoutResponse === "FAILED") {
            if (loginTriggerRef.current.started) return;
            loginTriggerRef.current.started = true;
            if (loginTriggerRef.current.fallbackTimer) {
                clearTimeout(loginTriggerRef.current.fallbackTimer);
                loginTriggerRef.current.fallbackTimer = undefined;
            }
            login();
        }
    }, [globalLogoutResponse]);

    const [securePasswordEntry, setSecurePasswordEntry] = React.useState(true);
    const [showLoadingIndicator, setShowLoadingIndicator] = useState(false);

    const [formData, setData] = React.useState<LoginFormState>({
        username: '',
        password: ''
    });

    const [errors, setErrors] = React.useState<LoginFormErrors>({});

    const validate = () => {
        const nextErrors: LoginFormErrors = {};

        if (!formData.username?.trim()) nextErrors.username = 'Username is required';
        if (!formData.password) {
            nextErrors.password = 'Password is required';
        } else if (formData.password.length < 8) {
            nextErrors.password = 'Password must be at least 8 characters';
        }

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const updateField = (field: keyof LoginFormState, value: string) => {
        setData({ ...formData, [field]: value });
        if (errors[field] || errors.formError) {
            setErrors({ ...errors, [field]: undefined, formError: undefined });
        }
    };

    const getFriendlyLoginError = (error: any) => {
        const code = String(error?.code || '');
        const message = String(error?.message || '');
        if (code === 'NotAuthorizedException' || /incorrect username or password/i.test(message)) {
            return getText('invalidCredentials');
        }
        if (code === 'UserNotConfirmedException') return getText('accountNotConfirmed');
        if (/network|fetch|service unavailable/i.test(message)) return getText('networkLoginError');
        return getText('loginUnexpectedError');
    };

    const dispatch = useDispatch();

    const resetState = async () => {
        try {
            await clearAppData();
            dispatch(AppActions.logout());
        } catch (e) {
            console.log('error signing out: ', e);
        }
    }

    const clearAppData = async function () {
        try {
            const lastProfile = await AsyncStorage.getItem(LAST_GRAIN_PROFILE_STORAGE_KEY);
            const keys = await AsyncStorage.getAllKeys();
            await AsyncStorage.multiRemove(keys);
            await AsyncStorage.clear();
            if (lastProfile) {
                await AsyncStorage.setItem(LAST_GRAIN_PROFILE_STORAGE_KEY, lastProfile);
            }
            console.log('App data cleared.');
        } catch (error) {
            console.error('Error clearing app data.');
        }
    }

    const login = async (retryCount = 0) => {
        try {
            if (isElectron()) {
                // Desktop offline-first: never clear cached/offline data on re-auth.
                dispatch(AppActions.forceLogoutHandled());
                dispatch(AppActions.resetCloudAuthStatus());
                // Best-effort local sign-out to avoid stale Amplify session state.
                try {
                    await Authorization.signOut();
                } catch {
                    // ignore
                }
            } else {
                await resetState()
            }
            const resp = await Authorization.signIn(formData.username, formData.password)
            if ('challengeName' in resp && resp['challengeName'] === "NEW_PASSWORD_REQUIRED") {
                navigation.navigate('ForceChangePassword', {
                    user: resp
                })
                setData({
                    ...formData,
                    password: ''
                })
                setShowLoadingIndicator(false)
                return
            }
            if (resp['signInUserSession'] !== undefined) {
                await AsyncStorage.removeItem(EXPLICIT_LOGOUT_STORAGE_KEY);
                // Clear any previous force logout popup state
                dispatch(AppActions.forceLogoutHandled());
                dispatch(AppActions.setCloudAuthStatus({
                    status: 'valid',
                    reason: undefined,
                    updatedAt: Date.now(),
                }));
                dispatch(LoginActions.setUserLoggedIn("LOGGED_IN"));
                console.log('user logged in')
            } else {
                dispatch(LoginActions.setUserLoggedIn("NOT_LOGGED_IN"));
                setErrors({
                    formError: LOGIN_FAILED_MESSAGE
                });
            }
        } catch (e: any) {
            if (e.message === "Access Token has been revoked") {
                if (retryCount < 3) {
                    await login(retryCount + 1)
                    return
                }
            }

            setErrors({
                ...errors,
                formError: getFriendlyLoginError(e)
            });
            setShowLoadingIndicator(false)
            dispatch(LoginActions.setUserLoggedIn("NOT_LOGGED_IN"));
        }
    }

    const handleLogin = async () => {
        setShowLoadingIndicator(true)
        if (isElectron()) {
            await login();
        } else {
            loginTriggerRef.current.started = false;
            if (loginTriggerRef.current.fallbackTimer) {
                clearTimeout(loginTriggerRef.current.fallbackTimer);
            }
            loginTriggerRef.current.fallbackTimer = setTimeout(() => {
                if (loginTriggerRef.current.started) return;
                loginTriggerRef.current.started = true;
                login();
            }, 5000);
            dispatch(LoginActions.globalLogout.request(formData.username));
        }
    }

    const handleForgotPassword = async () => {
        navigation.navigate('ForgotPassword')
    }

    const handleSignupClicked = async () => {
        navigation.navigate('Signup')
    }

    const onSubmit = () => {
        const isValid = validate();
        if (isValid) {
            // submit the data
            handleLogin()
        }
    };

    return (
        <Box variant={"main"}
            style={containerStyle.mainContainer}>
            <ProgressBar isVisible={showLoadingIndicator} progressText={getText('loggingIn')} />
            <ScrollView automaticallyAdjustKeyboardInsets={false}>
                <InweonLogo title={getText('signInTitle')} />
                <VStack
                    width={formProps.width}
                    style={containerStyle.formContainer} mx="3">
                    <FormControl isRequired>
                        <FormControl.Label>{getText('usernameLabel')}</FormControl.Label>
                        <Input
                            style={inputStyle.nativeInput}
                            isInvalid={'username' in errors}
                            placeholder={getText('usernamePlaceholder')}
                            onChangeText={value => updateField('username', value)} />
                        {'username' in errors ? <FormControl.ErrorMessage>{errors['username']}</FormControl.ErrorMessage> : null}

                        <FormControl.Label>{getText('passwordLabel')}</FormControl.Label>
                        <Input
                            secureTextEntry={securePasswordEntry}
                            style={inputStyle.nativeInput}
                            isInvalid={'password' in errors}
                            InputRightElement={
                                <IconButton
                                    onPress={() => {
                                        setSecurePasswordEntry(!securePasswordEntry)
                                    }}
                                    icon={
                                        <Icon
                                            as={MaterialCommunityIcons}
                                            name={securePasswordEntry ? "eye" : "eye-off"}
                                            color="coolGray.500"
                                        />
                                    } />
                            }
                            placeholder="********"
                            onSubmitEditing={onSubmit}
                            onChangeText={value => updateField('password', value)} />
                        {'password' in errors ? <FormControl.ErrorMessage>{errors['password']}</FormControl.ErrorMessage> : null}
                    </FormControl>
                    <Button
                        onPress={onSubmit}
                        mt="5">
                        {getText('submit')}
                    </Button>
                    {'formError' in errors ? <Text color="red.500" mt="2">{errors['formError'] || ""}</Text> : null}
                </VStack>

                <Button
                    variant="link"
                    onPress={handleForgotPassword}>
                    {getText('forgotPassword')}
                </Button>
                <Button variant="link"
                    onPress={handleSignupClicked}>
                    {getText('signUpLink')}
                </Button>
            </ScrollView>
        </Box>
    );
}
